import { HistoryRecord, InsulinScheduleView } from '../types/domain';

/**
 * Gamification (FR-19, URS §9 item 6).
 *
 * Points and badges are *derived* from the patient's own log, never stored.
 * That is deliberate:
 *   - offline, a reading the patient just queued already counts, because the
 *     history cache is updated optimistically (dataService.addInjection);
 *   - two devices cannot disagree, and there is no new write path that would
 *     have to join the offline queue;
 *   - changing a rule re-scores the whole history consistently.
 *
 * Every rule is capped per day so that recording the same thing ten times
 * earns nothing extra — the score rewards a complete, regular log, not volume.
 */

/** Points per rule. Tuned here, nowhere else. */
export const POINTS = {
  /** Per glucose reading, at most one per measurement context per day. */
  glucose: 1,
  /** Per insulin dose, at most one per schedule slot per day. */
  insulin: 1,
  /** Per counted dose whose injection point differs from the previous one. */
  rotation: 1,
  /** Bonus for a day with glucose checked and every scheduled dose logged. */
  completeDay: 3,
  /** Per recorded day that continues an unbroken streak (day 2 onwards). */
  streakDay: 1
} as const;

export type PointRule = keyof typeof POINTS;

export interface Badge {
  id: string;
  /** Consecutive recorded days needed to earn it. */
  days: number;
  title: string;
  emoji: string;
}

export const BADGES: Badge[] = [
  { id: 'streak-7', days: 7, title: 'Lencana 7 Hari', emoji: '🏅' },
  { id: 'streak-30', days: 30, title: 'Lencana 30 Hari', emoji: '🏆' }
];

export interface BadgeStatus extends Badge {
  earned: boolean;
  /** Days of the current streak counted toward it, capped at `days`. */
  progress: number;
}

export type PointBreakdown = Record<PointRule, number>;

export interface DayScore {
  date: string;
  points: PointBreakdown;
  total: number;
  hasGlucose: boolean;
  hasInsulin: boolean;
  complete: boolean;
}

export interface GamificationSummary {
  totalPoints: number;
  breakdown: PointBreakdown;
  /** Unbroken run of recorded days ending today, or yesterday if today is still open. */
  currentStreak: number;
  longestStreak: number;
  recordedDays: number;
  badges: BadgeStatus[];
  /** The first badge not yet earned, for the "keep going" line. */
  nextBadge: BadgeStatus | null;
  today: DayScore;
}

/** The labels the rule list shows the child. */
export const POINT_RULE_LABELS: Record<PointRule, string> = {
  glucose: 'Mencatat gula darah sesuai jadwal',
  insulin: 'Mencatat penggunaan insulin',
  rotation: 'Rotasi lokasi suntikan',
  completeDay: 'Melengkapi catatan harian',
  streakDay: 'Mempertahankan pencatatan beruntun'
};

const emptyBreakdown = (): PointBreakdown => ({
  glucose: 0,
  insulin: 0,
  rotation: 0,
  completeDay: 0,
  streakDay: 0
});

const sumBreakdown = (b: PointBreakdown): number =>
  b.glucose + b.insulin + b.rotation + b.completeDay + b.streakDay;

const emptyDay = (date: string): DayScore => ({
  date,
  points: emptyBreakdown(),
  total: 0,
  hasGlucose: false,
  hasInsulin: false,
  complete: false
});

const dayNumber = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};

const isGlucose = (r: HistoryRecord) =>
  r.glucoseValue !== undefined && r.glucoseValue !== null && !Number.isNaN(Number(r.glucoseValue));

const isInsulin = (r: HistoryRecord) => Number(r.insulinDose) > 0;

/**
 * Where a dose went. FR-09 grid rows use the coordinate; pre-FR-08 rows the
 * named-site id. Null when neither was recorded, which earns no rotation point.
 */
const siteKey = (r: HistoryRecord): string | null => {
  if (r.regionId) {
    return `${r.regionId}:${r.pointRow ?? ''}:${r.pointCol ?? ''}`;
  }
  if (r.siteId !== null && r.siteId !== undefined) {
    return `site:${r.siteId}`;
  }
  return null;
};

/**
 * Scores a patient's whole log.
 *
 * `today` is the patient's local date (YYYY-MM-DD). Rows dated after it are
 * ignored — a date picked in the future must not bank points or streak days.
 *
 * The complete-day bonus is judged against the *current* insulin schedule, as
 * the app keeps no history of past regimens. With no schedule, any dose counts.
 */
export function computeGamification(
  history: HistoryRecord[],
  schedule: InsulinScheduleView[],
  today: string
): GamificationSummary {
  const rows = history
    .filter(r => r.date && r.date <= today)
    .slice()
    .sort((a, b) =>
      a.date === b.date ? (a.time ?? '').localeCompare(b.time ?? '') : a.date.localeCompare(b.date)
    );

  const scheduledSlots = schedule.filter(s => s.active).map(s => s.slot as string);

  const byDate = new Map<string, HistoryRecord[]>();
  for (const row of rows) {
    const day = byDate.get(row.date);
    if (day) {
      day.push(row);
    } else {
      byDate.set(row.date, [row]);
    }
  }

  const breakdown = emptyBreakdown();
  let todayScore = emptyDay(today);
  let previousSite: string | null = null;
  let previousDay: number | null = null;
  let run = 0;
  let longestStreak = 0;

  for (const [date, dayRows] of byDate) {
    const score = emptyDay(date);

    const glucoseContexts = new Set<string>();
    const insulinSlots = new Set<string>();

    for (const row of dayRows) {
      if (isGlucose(row)) {
        const context = row.measurementContext ?? 'tidak-tercatat';
        if (!glucoseContexts.has(context)) {
          glucoseContexts.add(context);
          score.points.glucose += POINTS.glucose;
        }
      }

      if (isInsulin(row)) {
        const site = siteKey(row);
        // A row with no slot (pre-FR-04) is its own dose.
        const slot = row.insulinTypeId ?? `tanpa-slot:${row.time}`;
        if (!insulinSlots.has(slot)) {
          insulinSlots.add(slot);
          score.points.insulin += POINTS.insulin;
          // Only a dose that counted can earn rotation, so repeating a dose
          // cannot farm it.
          if (site !== null && site !== previousSite) {
            score.points.rotation += POINTS.rotation;
          }
        }
        previousSite = site;
      }
    }

    score.hasGlucose = glucoseContexts.size > 0;
    score.hasInsulin = insulinSlots.size > 0;
    const insulinDone =
      scheduledSlots.length > 0
        ? scheduledSlots.every(slot => insulinSlots.has(slot))
        : score.hasInsulin;
    score.complete = score.hasGlucose && insulinDone;
    if (score.complete) {
      score.points.completeDay += POINTS.completeDay;
    }

    const n = dayNumber(date);
    run = previousDay !== null && n - previousDay === 1 ? run + 1 : 1;
    previousDay = n;
    longestStreak = Math.max(longestStreak, run);
    if (run >= 2) {
      score.points.streakDay += POINTS.streakDay;
    }

    score.total = sumBreakdown(score.points);
    (Object.keys(breakdown) as PointRule[]).forEach(rule => {
      breakdown[rule] += score.points[rule];
    });

    if (date === today) {
      todayScore = score;
    }
  }

  // A streak survives until the end of the day after its last entry: a child
  // who has not recorded yet this morning has not broken anything.
  const todayNumber = dayNumber(today);
  const currentStreak =
    previousDay !== null && todayNumber - previousDay <= 1 ? run : 0;

  const badges: BadgeStatus[] = BADGES.map(badge => ({
    ...badge,
    earned: longestStreak >= badge.days,
    progress: Math.min(currentStreak, badge.days)
  }));

  return {
    totalPoints: sumBreakdown(breakdown),
    breakdown,
    currentStreak,
    longestStreak,
    recordedDays: byDate.size,
    badges,
    nextBadge: badges.find(b => !b.earned) ?? null,
    today: todayScore
  };
}

/** The patient's local calendar date, YYYY-MM-DD. */
export function localToday(now: Date = new Date()): string {
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
